'use client';

import type { VariantProps } from 'class-variance-authority';
import type { FC, ReactNode } from 'react';
import type { ModalMotionPreset } from '../modalMotion';
import { cva } from 'class-variance-authority';
import { useEffect } from 'react';
import {
  Dialog,
  DialogContent,

  DialogHeader,
} from '@/components/ui/Dialog/dialog';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
} from '@/components/ui/Drawer/drawer';
import { useIsMobile } from '@/hooks/useIsMobile';
import { cn } from '@/utils/cn';
import ModalBody from './ModalBody';
import ModalHeader from './ModalHeader';

export type ModalInnerProps = VariantProps<typeof dialogContentVariants> & VariantProps<typeof drawerVariants> & {
  isOpen: boolean;
  headerTitle?: string;
  accessibleTitle?: string;
  bodyClassName?: string;
  bodyOverflow?: 'auto' | 'visible';
  contentClassName?: string;
  headerClassName?: string;
  headerChild?: ReactNode;
  children: ReactNode;
  onboardingTmp?: ReactNode;
  onCloseAction: () => void;
  onHeaderBack?: () => void;
  onAnimationEnd?: () => void;
  hideCloseIcon?: boolean;
  contentMobileClassName?: string;
  disableOutsidePointerEvents?: boolean;
  motionPreset?: ModalMotionPreset;
};

const dialogContentVariants = cva(
  'bg-c900 text-white border-none flex rounded-2xl overflow-visible !max-h-[calc(100vh-4rem)] my-0',
  {
    variants: {
      variant: {
        default: 'bg-c900',
        gradient: 'bg-popup-desktop-gradient',
      },

    },
    compoundVariants: [
      {
        variant: 'default',
        class: 'w-fit',
      },
    ],
    defaultVariants: {
      variant: 'default',
    },
  },
);

const drawerVariants = cva(
  'text-white border-none min-h-0',
  {
    variants: {
      variant: {
        default: 'bg-c900',
        gradient: 'bg-popup-desktop-gradient',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

export const ModalInner: FC<ModalInnerProps> = ({
  variant,
  isOpen,
  headerTitle,
  accessibleTitle,
  bodyClassName,
  bodyOverflow = 'auto',
  contentClassName,
  contentMobileClassName,
  headerClassName,
  headerChild,
  children,
  onboardingTmp,
  onCloseAction,
  onHeaderBack,
  onAnimationEnd,
  hideCloseIcon,
  disableOutsidePointerEvents,
  motionPreset,
}) => {
  const isMobile = useIsMobile();
  const bodyOverflowClassName = bodyOverflow === 'visible' ? 'overflow-visible' : 'overflow-y-auto';

  const onOpenChange = (open: boolean) => {
    if (!open) {
      onCloseAction();
    }
  };

  useEffect(() => {
    if (!disableOutsidePointerEvents) {
      return undefined;
    }

    const timeoutId = setTimeout(() => {
      document.body.style.pointerEvents = '';
    }, 0);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [disableOutsidePointerEvents]);

  if (!isMobile) {
    return (
      <Dialog open={isOpen} onOpenChange={onOpenChange}>
        <DialogContent
          motionPreset={motionPreset}
          className={dialogContentVariants({ variant, className: cn('overflow-x-hidden custom-scrollbar', contentClassName) })}
          onInteractOutside={(e) => {
            e.preventDefault();
          }}
        >
          {onboardingTmp}
          <div className="grid grid-rows-[auto_1fr] w-full relative">
            <DialogHeader>
              <ModalHeader
                headerTitle={headerTitle}
                accessibleTitle={accessibleTitle}
                headerChild={headerChild}
                headerBackAction={onHeaderBack}
                onCloseAction={onCloseAction}
                headerClassName={headerClassName}
                hideCloseIcon={hideCloseIcon}
              />
            </DialogHeader>
            <div className={cn('min-h-0 flex-1 custom-scrollbar', bodyOverflowClassName)}>
              <ModalBody bodyClassName={bodyClassName}>{children}</ModalBody>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Drawer repositionInputs={false} dismissible={false} open={isOpen} onOpenChange={onOpenChange} onAnimationEnd={onAnimationEnd}>
      <DrawerContent
        motionPreset={motionPreset}
        onInteractOutside={e => e.preventDefault()}
        className={cn(
          'text-white border-none min-h-0',
          drawerVariants({ variant }),
          contentClassName,
        )}
      >
        <div className={cn('min-h-0 flex flex-col h-dvh overflow-x-hidden overflow-y-auto', contentMobileClassName)}>
          <DrawerHeader>
            <ModalHeader
              headerTitle={headerTitle}
              accessibleTitle={accessibleTitle}
              headerChild={headerChild}
              headerClassName={headerClassName}
              headerBackAction={onHeaderBack}
              onCloseAction={onCloseAction}
              hideCloseIcon={hideCloseIcon}
            />
          </DrawerHeader>
          <div className="min-h-0 flex-1">
            <ModalBody bodyClassName={bodyClassName}>{children}</ModalBody>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
};

ModalInner.displayName = 'ModalInner';

export default ModalInner;
